import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { RefreshCw, Zap, Gauge, ListTree, RotateCcw, PlayCircle, AlertTriangle } from "lucide-react";

export default function AdminSommelier() {
    const [stats, setStats] = useState(null);
    const [saving, setSaving] = useState(false);
    const [reindexing, setReindexing] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [form, setForm] = useState({ enabled: true, daily_token_limit: 500000, monthly_token_limit: 10000000, rate_per_min: 10 });

    const load = () => {
        api.get("/admin/sommelier/stats")
            .then(({ data }) => {
                setStats(data);
                setForm({
                    enabled: data.config.enabled,
                    daily_token_limit: data.config.daily_token_limit,
                    monthly_token_limit: data.config.monthly_token_limit,
                    rate_per_min: data.config.rate_per_min,
                });
            })
            .catch(() => toast.error("Errore caricamento stats"));
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        try {
            await api.put("/admin/config", {
                sommelier_enabled: form.enabled,
                sommelier_daily_token_limit: Number(form.daily_token_limit),
                sommelier_monthly_token_limit: Number(form.monthly_token_limit),
                sommelier_rate_per_min: Number(form.rate_per_min),
            });
            toast.success("Configurazione salvata");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally {
            setSaving(false);
        }
    };

    const reindex = async () => {
        setReindexing(true);
        try {
            const { data } = await api.post("/admin/sommelier/reindex");
            toast.success(`Indice ricostruito: ${data.count} vini`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally { setReindexing(false); }
    };

    const resetUsage = async () => {
        setResetting(true);
        try {
            await api.post("/admin/sommelier/reset-usage");
            toast.success("Contatore token azzerato");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally { setResetting(false); }
    };

    if (!stats) return <div className="text-[hsl(var(--muted-foreground))]">Caricamento…</div>;

    const dailyPct = Math.min(100, (stats.usage.daily_tokens / (stats.config.daily_token_limit || 1)) * 100);
    const monthlyPct = Math.min(100, (stats.usage.monthly_tokens / (stats.config.monthly_token_limit || 1)) * 100);

    return (
        <div className="max-w-3xl" data-testid="admin-sommelier-page">
            <h1 className="text-3xl mb-2">Assistente Sommelier</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6 max-w-xl">
                Chat AI in italiano che raccomanda vini della carta rispettando la visibilità
                dei prezzi per ruolo. Consuma token del budget Gemini configurato.
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-4">
                    <div className="co-caption flex items-center gap-2"><Gauge className="w-3.5 h-3.5" /> Uso oggi</div>
                    <div className="text-2xl mt-1">{stats.usage.daily_tokens.toLocaleString("it-IT")} <span className="text-sm text-[hsl(var(--muted-foreground))]">/ {stats.config.daily_token_limit.toLocaleString("it-IT")} token</span></div>
                    <div className="mt-2 h-1 bg-[hsl(var(--secondary))] rounded-full overflow-hidden">
                        <div className="h-full bg-[hsl(var(--primary))]" style={{ width: `${dailyPct}%` }} />
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-2">{stats.usage.daily_messages} messaggi</div>
                </div>
                <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-4">
                    <div className="co-caption flex items-center gap-2"><Gauge className="w-3.5 h-3.5" /> Uso mese</div>
                    <div className="text-2xl mt-1">{stats.usage.monthly_tokens.toLocaleString("it-IT")} <span className="text-sm text-[hsl(var(--muted-foreground))]">/ {stats.config.monthly_token_limit.toLocaleString("it-IT")} token</span></div>
                    <div className="mt-2 h-1 bg-[hsl(var(--secondary))] rounded-full overflow-hidden">
                        <div className="h-full bg-[hsl(var(--primary))]" style={{ width: `${monthlyPct}%` }} />
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-2">{stats.usage.monthly_messages} messaggi</div>
                </div>
            </div>

            <div className="mb-6 flex justify-end">
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <button
                            className="co-btn-ghost text-sm inline-flex items-center gap-2"
                            disabled={resetting}
                            data-testid="sommelier-reset-usage-btn"
                        >
                            <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                            {resetting ? "Reset in corso…" : "Reset contatore"}
                        </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="sommelier-reset-usage-dialog">
                        <AlertDialogHeader>
                            <AlertDialogTitle>Azzerare il contatore token del Sommelier?</AlertDialogTitle>
                            <AlertDialogDescription>
                                L'operazione cancella i contatori <b>giornaliero</b> e <b>mensile</b> di
                                token e messaggi, e resetta i buffer del rate-limit. Utile dopo test o
                                se il budget è stato consumato per errore. Non è reversibile.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid="sommelier-reset-usage-cancel">Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={resetUsage} data-testid="sommelier-reset-usage-confirm">
                                Sì, azzera
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>

            <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-4 mb-6 flex items-center gap-4">
                <ListTree className="w-4 h-4 text-[hsl(var(--muted-foreground))]" strokeWidth={1.5} />
                <div className="text-sm flex-1">
                    <div>Indice carta · <b>{stats.index.count}</b> vini</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">Ricostruito {stats.index.built_at ? new Date(stats.index.built_at).toLocaleString("it-IT") : "—"}</div>
                </div>
                <button onClick={reindex} disabled={reindexing} className="co-btn-ghost text-sm" data-testid="sommelier-reindex-btn">
                    <RefreshCw className="w-4 h-4" /> {reindexing ? "…" : "Ricostruisci ora"}
                </button>
            </div>

            <form onSubmit={save} className="bg-white border border-[hsl(var(--border))] rounded-sm p-5 space-y-4">
                <h2 className="text-xl flex items-center gap-2"><Zap className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} /> Configurazione</h2>
                <div className="flex items-center gap-3">
                    <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="sommelier-enabled-toggle" />
                    <label className="text-sm">Assistente attivo</label>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                        <label className="co-caption text-xs">Tetto token giornaliero</label>
                        <Input type="number" min="0" value={form.daily_token_limit} onChange={(e) => setForm({ ...form, daily_token_limit: e.target.value })} data-testid="sommelier-daily-limit" />
                    </div>
                    <div>
                        <label className="co-caption text-xs">Tetto token mensile</label>
                        <Input type="number" min="0" value={form.monthly_token_limit} onChange={(e) => setForm({ ...form, monthly_token_limit: e.target.value })} data-testid="sommelier-monthly-limit" />
                    </div>
                    <div>
                        <label className="co-caption text-xs">Messaggi/minuto</label>
                        <Input type="number" min="1" max="60" value={form.rate_per_min} onChange={(e) => setForm({ ...form, rate_per_min: e.target.value })} data-testid="sommelier-rate-limit" />
                    </div>
                </div>
                <button type="submit" disabled={saving} className="co-btn-primary" data-testid="sommelier-save-btn">
                    {saving ? "Salvataggio…" : "Salva"}
                </button>
            </form>

            <SommelierGuidanceSection cfg={stats.config} onSaved={load} />
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Fase 6.8 pt.8 · Linee guida + Vini da proporre                    */
/* ------------------------------------------------------------------ */

function SommelierGuidanceSection({ cfg, onSaved }) {
    // cfg here is stats.config which currently doesn't expose guidelines/promoted
    // so we fetch them from /admin/config directly.
    const [guidelines, setGuidelines] = useState("");
    const [promotedIds, setPromotedIds] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [query, setQuery] = useState("");
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/admin/config").then(({ data }) => {
            setGuidelines(data.sommelier_guidelines || "");
            setPromotedIds(data.sommelier_promoted_wine_ids || []);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    // Fetch candidate wines when query changes (min 2 chars)
    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) { setCandidates([]); return; }
        const t = setTimeout(() => {
            api.get("/admin/wines/search", { params: { q, limit: 15 } })
                .then(({ data }) => setCandidates(data.items || []))
                .catch(() => setCandidates([]));
        }, 250);
        return () => clearTimeout(t);
    }, [query]);

    // Fetch promoted details for display (name/producer/vintage). Fase 8.9:
    // 404 → id orfano (vino cancellato). Lo teniamo nel form ma flaggiato.
    const [promotedDetails, setPromotedDetails] = useState({});
    useEffect(() => {
        if (promotedIds.length === 0) { setPromotedDetails({}); return; }
        Promise.all(promotedIds.map((id) =>
            api.get(`/admin/wines/${id}`)
                .then((r) => [id, { ...r.data, __missing: false }])
                .catch((err) => [id, { __missing: true, __status: err.response?.status || 0 }])
        )).then((entries) => {
            const map = {};
            for (const [id, w] of entries) map[id] = w;
            setPromotedDetails(map);
        });
    }, [promotedIds]);

    const orphanIds = promotedIds.filter((id) => promotedDetails[id]?.__missing);
    const cleanOrphans = () => {
        if (orphanIds.length === 0) return;
        setPromotedIds(promotedIds.filter((id) => !orphanIds.includes(id)));
        toast.success(`Rimossi ${orphanIds.length} ID orfani (salva per confermare)`);
    };

    // Fase 8.9 · Simula query — anteprima retrieval + promoted senza consumare token.
    const [simQuery, setSimQuery] = useState("");
    const [simResult, setSimResult] = useState(null);
    const [simulating, setSimulating] = useState(false);
    const simulate = async () => {
        const msg = simQuery.trim();
        if (!msg) return;
        setSimulating(true);
        setSimResult(null);
        try {
            const { data } = await api.post("/admin/sommelier/simulate", { message: msg });
            setSimResult(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore simulazione");
        } finally { setSimulating(false); }
    };

    const addWine = (w) => {
        if (promotedIds.includes(w.id)) return;
        setPromotedIds([...promotedIds, w.id]);
        setQuery("");
        setCandidates([]);
    };
    const removeWine = (id) => setPromotedIds(promotedIds.filter((x) => x !== id));
    const move = (idx, dir) => {
        const j = idx + dir;
        if (j < 0 || j >= promotedIds.length) return;
        const next = [...promotedIds];
        [next[idx], next[j]] = [next[j], next[idx]];
        setPromotedIds(next);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/config", {
                sommelier_guidelines: guidelines,
                sommelier_promoted_wine_ids: promotedIds,
            });
            toast.success("Guida sommelier salvata");
            onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally { setSaving(false); }
    };

    if (loading) return null;
    return (
        <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-5 mt-6 space-y-6" data-testid="sommelier-guidance-section">
            <h2 className="text-xl">Guida al Sommelier</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] -mt-3">
                Il campo "istruzioni" qui sotto è il modo principale per governare
                l'agente: cosa dire, cosa non dire, quali abbinamenti proporre o
                evitare. Le istruzioni sono iniettate come regola ad alta priorità
                nel prompt di sistema. I "vini da proporre" invece sono un hint di
                priorità che il modello usa solo se pertinente alla richiesta.
            </p>

            <div>
                <label className="co-caption text-xs mb-2 block" htmlFor="sommelier-guidelines-textarea">
                    Istruzioni per l'agente — cosa fare e cosa NON fare
                </label>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2 leading-relaxed">
                    Testo libero iniettato ad alta priorità nel prompt di sistema del sommelier.
                    Usalo per fissare regole permanenti del ristorante: cosa citare, cosa evitare,
                    tono da tenere, abbinamenti da NON proporre. Il modello lo rispetta come
                    vincolo prioritario ad ogni risposta.
                </p>
                <textarea
                    id="sommelier-guidelines-textarea"
                    value={guidelines}
                    onChange={(e) => setGuidelines(e.target.value)}
                    rows={8}
                    placeholder={
                        "Esempi di istruzioni utili (scrivi liberamente le tue):\n" +
                        "• Non fare mai riferimenti ad abbinamenti con la carne.\n" +
                        "• La cucina è vegetariana: proponi solo abbinamenti coerenti (verdure, formaggi, legumi, pasta, risotti).\n" +
                        "• Consiglia solo i piatti del ristorante quando parli di abbinamenti.\n" +
                        "• Privilegia i produttori locali del Collio.\n" +
                        "• Evita bottiglie sopra i €150 salvo esplicita richiesta.\n" +
                        "• Suggerisci sempre un'alternativa al calice quando possibile."
                    }
                    className="w-full bg-white border border-[hsl(var(--border))] rounded-sm p-3 text-sm leading-relaxed"
                    data-testid="sommelier-guidelines-textarea"
                />
                <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1 italic">
                    Suggerimento: scrivi frasi brevi e imperative. Ogni riga è una regola.
                </p>
            </div>

            <div>
                <label className="co-caption text-xs mb-2 block">Vini da proporre ({promotedIds.length})</label>
                <div className="relative mb-2">
                    <Input
                        placeholder="Cerca vino da aggiungere (nome, produttore)…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        data-testid="sommelier-promoted-search"
                        className="bg-white"
                    />
                    {candidates.length > 0 && (
                        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-[hsl(var(--border))] rounded-sm shadow-lg max-h-72 overflow-y-auto" data-testid="sommelier-promoted-candidates">
                            {candidates.map((w) => (
                                <button
                                    key={w.id}
                                    type="button"
                                    onClick={() => addWine(w)}
                                    className="w-full text-left px-3 py-2 hover:bg-[hsl(var(--secondary))] text-sm border-b border-[hsl(var(--border))] last:border-0"
                                    disabled={promotedIds.includes(w.id)}
                                    data-testid={`sommelier-promoted-candidate-${w.id}`}
                                >
                                    <div className="font-medium truncate">
                                        {w.producer || "—"} · {w.name} {w.vintage && <span className="italic text-[hsl(var(--muted-foreground))]">{w.vintage}</span>}
                                    </div>
                                    {promotedIds.includes(w.id) && <div className="text-[10px] text-[hsl(var(--muted-foreground))]">già in lista</div>}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {promotedIds.length === 0 ? (
                    <div className="text-xs italic text-[hsl(var(--muted-foreground))] px-3 py-6 text-center border border-dashed border-[hsl(var(--border))] rounded-sm">
                        Nessun vino in evidenza. Cerca sopra per aggiungerne.
                    </div>
                ) : (
                    <>
                        {orphanIds.length > 0 && (
                            <div className="mb-3 p-3 rounded-sm border border-amber-600/40 bg-amber-50 flex items-start gap-2" data-testid="sommelier-promoted-orphan-banner">
                                <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" strokeWidth={1.5} />
                                <div className="flex-1 text-xs">
                                    <div className="font-medium">{orphanIds.length} ID orfani nella lista</div>
                                    <div className="text-[hsl(var(--muted-foreground))] mt-0.5">
                                        Questi vini non esistono piu' nel catalogo (cancellati o non piu' visibili al pubblico).
                                        Non appariranno nelle risposte del sommelier.
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={cleanOrphans}
                                    className="co-btn-ghost text-xs shrink-0"
                                    data-testid="sommelier-promoted-cleanup-orphans-btn"
                                >
                                    Rimuovi orfani
                                </button>
                            </div>
                        )}
                    <div className="divide-y divide-[hsl(var(--border))] border border-[hsl(var(--border))] rounded-sm" data-testid="sommelier-promoted-list">
                        {promotedIds.map((id, idx) => {
                            const w = promotedDetails[id];
                            const isOrphan = w?.__missing === true;
                            return (
                                <div
                                    key={id}
                                    className={`flex items-center gap-3 px-3 py-2 text-sm ${isOrphan ? "bg-amber-50/60" : ""}`}
                                    data-testid={`sommelier-promoted-row-${id}`}
                                >
                                    <span className="text-xs font-mono text-[hsl(var(--muted-foreground))] w-6">{idx + 1}.</span>
                                    <div className="flex-1 min-w-0">
                                        {isOrphan ? (
                                            <div className="text-amber-700 flex items-center gap-1.5" data-testid={`sommelier-promoted-orphan-${id}`}>
                                                <AlertTriangle className="w-3 h-3" strokeWidth={1.5} />
                                                <span className="text-xs">Vino non trovato · <code className="font-mono">{id.slice(0, 8)}…</code></span>
                                            </div>
                                        ) : w ? (
                                            <>
                                                <div className="font-medium truncate">{w.producer || "—"} · {w.name}</div>
                                                {w.vintage && <div className="text-[11px] italic text-[hsl(var(--muted-foreground))]">{w.vintage}</div>}
                                            </>
                                        ) : (
                                            <div className="italic text-[hsl(var(--muted-foreground))]">Vino {id.slice(0,8)}… · caricamento…</div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="px-2 py-1 text-xs hover:bg-[hsl(var(--secondary))] rounded-sm disabled:opacity-30" data-testid={`sommelier-promoted-up-${id}`}>↑</button>
                                        <button type="button" onClick={() => move(idx, +1)} disabled={idx === promotedIds.length - 1} className="px-2 py-1 text-xs hover:bg-[hsl(var(--secondary))] rounded-sm disabled:opacity-30" data-testid={`sommelier-promoted-down-${id}`}>↓</button>
                                        <button type="button" onClick={() => removeWine(id)} className="px-2 py-1 text-xs hover:bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] rounded-sm" data-testid={`sommelier-promoted-remove-${id}`}>×</button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    </>
                )}
            </div>

            {/* Fase 8.9 · Simulatore query — anteprima retrieval */}
            <div className="border-t border-[hsl(var(--border))] pt-4" data-testid="sommelier-simulate-section">
                <label className="co-caption text-xs mb-2 block">Simula domanda cliente (anteprima retrieval, no token AI)</label>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2 leading-relaxed">
                    Digita una domanda tipica e vedi in tempo reale se i "vini da proporre"
                    sarebbero inclusi (pertinenti alla richiesta) o esclusi (filtrati dal
                    vincolo tipologia/prezzo/al calice, oppure orfani).
                </p>
                <div className="flex gap-2 mb-3">
                    <Input
                        value={simQuery}
                        onChange={(e) => setSimQuery(e.target.value)}
                        placeholder='es. "Un rosso piemontese sotto 100 euro" oppure "un bianco al calice"'
                        onKeyDown={(e) => { if (e.key === "Enter") simulate(); }}
                        data-testid="sommelier-simulate-input"
                    />
                    <button
                        type="button"
                        onClick={simulate}
                        disabled={simulating || !simQuery.trim()}
                        className="co-btn-primary shrink-0"
                        data-testid="sommelier-simulate-btn"
                    >
                        <PlayCircle className="w-4 h-4" />
                        {simulating ? "Analisi…" : "Simula"}
                    </button>
                </div>
                {simResult && (
                    <div className="p-3 rounded-sm border border-[hsl(var(--border))] bg-[#FDFBF7] space-y-3" data-testid="sommelier-simulate-result">
                        <div className="text-xs">
                            <span className="co-caption">Vincoli estratti dalla query:</span>
                            <div className="font-mono text-[10px] mt-1 text-[hsl(var(--muted-foreground))]">
                                {JSON.stringify(simResult.constraints, null, 0)}
                            </div>
                        </div>
                        <div className="text-xs">
                            <span className="co-caption">Promoted inclusi: </span>
                            <b className="text-[hsl(var(--primary))]">{simResult.promoted_included}</b>
                            {" / "}
                            <span>{(simResult.promoted_ids_configured || []).length} configurati</span>
                        </div>
                        {simResult.promoted_excluded && simResult.promoted_excluded.length > 0 && (
                            <div className="text-xs">
                                <span className="co-caption">Promoted esclusi:</span>
                                <ul className="mt-1 space-y-0.5 pl-3">
                                    {simResult.promoted_excluded.map((x) => (
                                        <li key={x.id} className="text-[hsl(var(--muted-foreground))]" data-testid={`sim-excluded-${x.id}`}>
                                            <code className="font-mono text-[10px]">{x.id.slice(0, 8)}</code>
                                            {" · "}
                                            {x.name ? `${x.producer} · ${x.name}` : "(non piu' in DB)"}
                                            {" · "}
                                            <span className={x.reason === "orphan_not_in_index" ? "text-amber-700" : ""}>
                                                {x.reason === "filtered_out_by_query" ? "filtrato dalla query" :
                                                 x.reason === "orphan_not_in_index" ? "orfano / non in indice pubblico" : x.reason}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <div className="text-xs">
                            <span className="co-caption">Top 5 risultati che l'AI vedrebbe (⭐ = promoted):</span>
                            <ul className="mt-1 space-y-0.5 pl-3 font-mono text-[10px]">
                                {(simResult.retrieved || []).slice(0, 5).map((r, i) => (
                                    <li key={r.id}>
                                        #{i + 1}: <b>{r.type}</b> · {r.producer} · {r.name}{r.vintage ? ` ${r.vintage}` : ""}
                                        {r.is_promoted && <span className="text-[hsl(var(--primary))]"> ⭐PROMOTED</span>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </div>

            <button type="button" onClick={save} disabled={saving} className="co-btn-primary" data-testid="sommelier-guidance-save">
                {saving ? "Salvataggio…" : "Salva guida sommelier"}
            </button>
        </div>
    );
}
